import { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import { VscFolderActive } from 'react-icons/vsc';
import ReportsSidebar from './sidebarV3';
import FilterBar from './filterBar';
import { applyFilters } from '../../components/Reporting/applyFilters';
import ReportViewer from '../../components/Reporting/ReportViewer';
import isEqual from 'lodash/isEqual';



const reportConfigs = {
    conversion: {
      name: 'Conversion Funnel',
      filters: [
        'dateRange',
        'loanPurpose',
        'loanType',
        'loanSource',
        'state',
        'county',
        'city',
        'zip',
        'minPurchasePrice',
        'maxPurchasePrice',
        'minLoanAmount',
        'maxLoanAmount',
        'minInterestRate',
        'maxInterestRate'
      ],
    },
    leadSources: {
      name: 'Lead Sources Breakdown',
      filters: [
        'dateRange',
        'loanPurpose',
        'loanType',
        'loanSource',
        'referralSource',
        'state',
        'county',
        'city',
        'zip'
      ],
    },
    leaderboard: {
      name: 'Referral Leaderboard',
      filters: [
        'dateRange',
        'referralSource'
      ],
    },
    birthdays: {
      name: 'Birthday List',
      filters: [
        'dateRange',
        'state',
        'county',
        'city',
        'zip'
      ],
    },
    annualReview: {
      name: 'Annual Mortgage Review List',
      filters: [
        'dateRange',
        'loanPurpose',
        'loanType',
        'state',
        'county',
        'city',
        'zip'
      ],
    },
};


const useDebounce = ( value, delay = 300 ) => {
    const [ debouncedValue, setDebouncedValue ] = useState( value );

    useEffect( () => {
        const handler = setTimeout( () => setDebouncedValue( value ), delay );
        return () => clearTimeout( handler );
    }, [ value, delay ] );

    return debouncedValue;
};


const ReportsDashboard = ( { user, viewing, loans, contacts, opps, schema } ) => {
    const [ containerBuffer, setContainerBuffer ] = useState( 115 );
    const [ gridHeight, setGridHeight ] = useState( 0 );
    const defaultFilters = {
        dateRange: 'last90days',
        startDate: null,
        endDate: null,
        loanPurpose: [],
        loanType: [],
        loanSource: [],
        referralSource: [],
        state: [],
        county: [],
        city: [],
        zip: [],
        minPurchasePrice: '',
        maxPurchasePrice: '',
        minLoanAmount: '',
        maxLoanAmount: '',
    };
    const [ selectedReport, setSelectedReport ] = useState( null );
    const [ filters, setFilters ] = useState( defaultFilters );
    const [ dynamicOptions, setDynamicOptions ] = useState( {
        loanPurpose: [],
        loanType: [],
        loanSource: [],
        referralSource: [],
        cities: [],
        states: [],
        zips: [],
        counties: [],
    } );
    
    const prev = useRef( null );
    const containerRef = useRef();
    const titleRef = useRef();
    const filterRef = useRef();
    const numbersRef = useRef();

    const filteredData = useMemo( () => {
        if ( !selectedReport ) return [];
        
        const loanArray = [];
        const oppArray = [];
        const conArray = [];

        Object.keys( loans ).forEach( lid => {
            if ( viewing.includes( loans[lid].aid ) ) {
                loanArray.push( loans[lid] );
            }
        } );
        Object.keys( opps ).forEach( loid => {
            if ( viewing.includes( opps[loid].aid ) ) {
                oppArray.push( opps[loid] );
            }
        } );
        Object.keys( contacts ).forEach( cid => {
            if ( viewing.includes( contacts[cid].aid ) || contacts[cid].share === true ) {
                conArray.push( contacts[cid] );
            }
        } );

        const rawData = [];
        switch ( selectedReport.id ) {
            case 'pipe-fund-summary':
                for ( const l of loanArray ) {
                    const lo = oppArray.find( f => f.relationships?.find( fa => fa.lid === l.lid ) );
                    rawData.push( {
                        ...lo,
                        ...l,
                        loanSource: lo?.source,
                    } );
                }
                break;

            case 'partner-leaderboard':
                for ( const lo of oppArray ) {
                    let lid;
                    let refId;
                     
                    if ( !lo.relationships?.length ) {
                        continue;
                    }
                    
                    for ( const lor of lo.relationships ) {
                        if ( lor.lid ) {
                            lid = lor.lid;
                        }
                        const c = contacts[lor.cid];
                        if ( c?.referredById ) {
                            refId = c.referredById;
                        }
                    }

                    if ( !refId ) {
                        continue;
                    }

                    const c = contacts[refId];
                    const l = loans[lid];
                    rawData.push( { 
                        ...lo,
                        ...l,
                        loanSource: lo.source,
                        contact: c
                    } );
                }
                break;

            default:
        }
        
        if ( prev.current && isEqual( prev.current.filters, filters ) && isEqual( prev.current.rawData, rawData ) ) {
            return prev.current.result;
        }

        const result = applyFilters( rawData, filters, selectedReport );

        prev.current = {
            filters,
            rawData,
            result,
        };
    
        return result;
    }, [ selectedReport, filters, loans, contacts, opps, viewing ] );

    useEffect( () => {
        if ( user.additionalAccounts && user.additionalAccounts.length > 1 ) {
            setContainerBuffer( prev => prev + 30 );
        }
    }, [ user.additionalAccounts ] );

    useEffect( () => {
        setGridHeight( containerRef.current.clientHeight );
    }, [ containerBuffer ] );

    useEffect( () => {
        const obj = {
            loanPurpose: user.meta?.global.picklist.purpose || [],
            loanType: user.meta?.loan.picklist.loanType || [],
            loanSource: user.meta?.global.picklist.source || [],
            referralSource: [],
            cities: [],
            states: [],
            zips: [],
            counties: [],
        }
        setDynamicOptions( obj );
    }, [ user.meta ] );

    useEffect( () => {
        function capitalizeWords( str ) {
            return str.replace( /\b\w/g, ( char ) => char.toUpperCase() );
        }
        
        if ( loans ) {
            const cities = new Set();
            const states = new Set();
            const zips = new Set();
            const counties = new Set();

            Object.keys( loans ).forEach( lid => {
                const l = loans[lid];
                if ( !viewing.includes( l.aid ) ) {
                    return;
                }
                if ( l.subjectCity?.length ) {
                    cities.add( l.subjectCity.trim().toLowerCase() );
                }
                if ( l.subjectState?.length ) {
                    states.add( l.subjectState );
                }
                if ( l.subjectZip?.length ) {
                    zips.add( l.subjectZip );
                }
                if ( l.subjectCounty?.length ) {
                    counties.add( l.subjectCounty.trim().toLowerCase() );
                }
            } );

            setDynamicOptions( prev => {
                return {
                    ...prev,
                    cities: Array.from( cities ).sort( ( a, b ) => a.localeCompare( b ) ).map( c => { return { label: capitalizeWords( c ), value: c } } ),
                    states: Array.from( states ).sort( ( a, b ) => a.localeCompare( b ) ).map( m => { return { label: m, value: m } } ),
                    zips: Array.from( zips ).sort( ( a, b ) => a.localeCompare( b ) ).map( z => { return { label: z, value: z } } ),
                    counties: Array.from( counties ).sort( ( a, b ) => a.localeCompare( b ) ).map( c => { return { label: capitalizeWords( c ), value: c } } )
                }
            } )
        }
    }, [ loans, viewing ] );

    return (
        <Container fluid className='p-0' style={ { height: '100%' } }>
            <Row  style={ { height: `calc(100vh - ${containerBuffer}px)` } } ref={containerRef}>
                <Col className='p-0' xs={3} md={2} style={ { height: '100%', overflowY: 'auto' } }>
                    <ReportsSidebar 
                        sidebarConfig={user.meta?.reporting} 
                        selectedReportId={selectedReport?.id} 
                        onSelectReport={ ( report ) => {
                            setSelectedReport( report );
                            setFilters( defaultFilters );
                        } }
                    />
                </Col>
                <Col className='p-0' xs={9} md={10} style={ { height: '100%', overflowY: 'auto' } }>
                    <Container fluid className='p-0' style={ { height: '100%' } }>
                        <Row className='tf-section-container p-0 me-4'>
                            <FilterBar filters={filters} setFilters={setFilters} reportConfig={reportConfigs['conversion']} dynamicOptions={dynamicOptions} />
                        </Row>
                        <Row className='tf-section-container p-0 me-4'>
                            <ReportViewer
                                selectedReport={selectedReport}
                                filters={filters}
                                setFilters={setFilters}
                                schema={schema[selectedReport?.id]}
                                data={filteredData}
                            />
                        </Row>
                    </Container>
                </Col>
            </Row>
        </Container>
    )
};


const mapProps = ( state ) => ( {
    orgId: state.user.orgId,
    aid: state.user.aid,
    uid: state.user.email,
    user: state.user,
    grids: state.user.meta.datagrids,
    schema: state.user.meta.gridschema,
    viewing: state.user.viewing,
    canExport: state.user.canExport,
    loans: state.loans.byId,
    contacts: state.contacts.byId,
    opps: state.leadopps.byId,
    tasks: state.tasks.byId,
    status: state.user.meta.loan.picklist.status
} );
  
const mapDispatch = ( dispatch ) => ( {
    setDatagridWidth: dispatch.user.setDatagridWidth,
    setDatagridOrder: dispatch.user.setDatagridOrder
} );
  
export default connect( mapProps, mapDispatch )( ReportsDashboard );